import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { DialogTitle, Typography } from '@mui/material';

import _uniq from 'lodash/uniq';
import { useLocalStorage } from 'usehooks-ts';

import { AnalyticsContext, EVENTS, SignupFormComponent, useTrack } from '@arrived/analytics';
import { SIGNUP_POPUP_LOCAL_STORAGE_KEYS, stub } from '@arrived/common';
import { FEATURE_FLAGS, useFeature } from '@arrived/feature-flags';
import { OfferingId } from '@arrived/models';

import { SignupDialog } from '../Signup';

export interface OfferingViewLimitContextType {
  hasViewedMaxOfferings: boolean;
  /**
   * True when the popup is showing.
   */
  isPopupShowing: boolean;
  /**
   * Invoke when a property card is clicked with the offering id, will return a boolean if that property page can be
   * viewed.
   */
  shouldNavigateToOffering: (offeringId: OfferingId) => boolean;
  /**
   * When invoked, will show the signup pop-up modal.
   */
  showPopup: () => void;
  /**
   * The list of Offering ids that have already been viewed.
   */
  viewedOfferingIds: number[];
}

export const OfferingViewLimitContext = createContext<OfferingViewLimitContextType>({
  hasViewedMaxOfferings: false,
  isPopupShowing: false,
  shouldNavigateToOffering: () => false,
  showPopup: stub,
  viewedOfferingIds: [],
});

export const OfferingViewLimitContextProvider = ({ children }: PropsWithChildren) => {
  const [viewedOfferingIds, setViewedOfferingIds] = useLocalStorage<OfferingId[]>(
    SIGNUP_POPUP_LOCAL_STORAGE_KEYS.viewedOfferings,
    []
  );
  const { hasAnonymousUserLoggedIn, isLoading } = useContext(AnalyticsContext);
  const track = useTrack();
  const signupPopupFeatureFlag = useFeature<number>(FEATURE_FLAGS.SIGNUP_POP_UP);
  const [isPopupShowing, setIsPopupShowing] = useState(false);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  // We only want to enable the popup if the following criteria are met:
  // 1. The feature flag is on and the value is greater than or equal to 0.
  //  NOTE: We can't use `signupPopupFeatureFlag.on` here because that will be false if the value is 0, which we want
  //  to allow as a value.
  // 2. The user is NOT logged in.
  // 3. It is not the case that the user is part of an experimental group and rudder analytics is loading. If the user
  //  is part of an experiment, we need to wait until rudder analytics is loaded so that they get assigned to a group
  //  based on their anonymous ID.
  const isPopupEnabled =
    signupPopupFeatureFlag.source !== 'unknownFeature' &&
    signupPopupFeatureFlag.value >= 0 &&
    !hasAnonymousUserLoggedIn &&
    !(isLoading && signupPopupFeatureFlag.source === 'experiment');

  const shouldNavigateToOffering = useCallback(
    (offeringId: OfferingId) => {
      if (isPopupEnabled) {
        // If the user has already viewed the offering, allow them to navigate to that page. Otherwise if they
        if (viewedOfferingIds.length >= signupPopupFeatureFlag.value && !viewedOfferingIds.includes(offeringId)) {
          return false;
        } else {
          // We only want to record the offering ids when the popup is enabled, this is to avoid the scenario in which
          // a user who originally is not restricted by an offering view count looks at a bunch of offerings, is later
          // restricted but can now see all the offerings that they previously viewed. An edge case, but still one that
          // we should try to avoid.
          setViewedOfferingIds((viewedOfferingIds) => _uniq([...viewedOfferingIds, offeringId]));
        }
      }
      return true;
    },
    [isPopupEnabled, signupPopupFeatureFlag, viewedOfferingIds]
  );

  useEffect(() => {
    // Only track after Rudderstack has loaded so that we correctly attribute them to their assigned group.
    if (!isLoading && signupPopupFeatureFlag.source === 'experiment') {
      track(EVENTS.EXPERIMENT_VIEWED, {
        experimentName: FEATURE_FLAGS.SIGNUP_POP_UP,
        experimentGroupNumber: signupPopupFeatureFlag.value,
      });
    }
  }, [isLoading, signupPopupFeatureFlag]);

  useEffect(() => {
    if (isPopupShowing) {
      // Need to delay the focus to the next render so that the input can render and get attached to the ref before we
      // try to focus it.
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 0);
    }
  }, [isPopupShowing]);

  const showPopup = useCallback(() => {
    if (isPopupEnabled) {
      setIsPopupShowing(true);
      track(EVENTS.SIGNUP_FORM_DISPLAYED, {
        action: 'open',
        component: SignupFormComponent.OFFERING_VIEW_LIMIT_POP_UP,
      });
    }
  }, [isPopupEnabled, track]);
  const handleOnClose = useCallback(() => {
    setIsPopupShowing(false);
    track(EVENTS.SIGNUP_FORM_DISPLAYED, { action: 'close', component: SignupFormComponent.OFFERING_VIEW_LIMIT_POP_UP });
  }, [track]);

  const value = useMemo(
    () => ({
      hasViewedMaxOfferings: isPopupEnabled && viewedOfferingIds.length >= signupPopupFeatureFlag.value,
      isPopupShowing,
      shouldNavigateToOffering,
      showPopup,
      viewedOfferingIds,
    }),
    [isPopupEnabled, isPopupShowing, shouldNavigateToOffering, showPopup, signupPopupFeatureFlag, viewedOfferingIds]
  );

  return (
    <OfferingViewLimitContext.Provider value={value}>
      <SignupDialog
        accountCreationEventProperties={{ component: SignupFormComponent.OFFERING_VIEW_LIMIT_POP_UP }}
        close={handleOnClose}
        DialogContentProps={{
          children: (
            <Typography variant="h5">
              <FormattedMessage id="signup-for-full-access" defaultMessage="Sign up for full access" />
            </Typography>
          ),
        }}
        open={isPopupShowing}
      >
        <DialogTitle variant="h3.bold" textAlign="center" sx={{ px: 0, py: 0, mb: { xs: 0, md: 1 } }}>
          <FormattedMessage id="unlock-all-homes" defaultMessage="Unlock all homes" />
        </DialogTitle>
      </SignupDialog>
      {children}
    </OfferingViewLimitContext.Provider>
  );
};
